import { useLocale } from "@/components/i18n/LocaleProvider";
import { nonbreakingSpace } from "@/lib/consts";
import type { Locale } from "@/lib/i18n/locales";
import { omit } from "lodash-es";
import { useCallback, useMemo } from "react";

function intlLocaleForSiteLocale(locale: Locale): string {
  switch (locale) {
    case "de":
      return "de-DE";
    case "en":
      return "en-GB";
    case "fr":
      return "fr-FR";
    case "zh":
      return "zh-CN";
    case "ja":
      return "ja-JP";
    default:
      return "de-DE";
  }
}

/**
 * Options for formatting numbers.
 *
 * @property {string} [locale="de-DE"] - The locale string that determines the number formatting style. Defaults to "de-DE".
 * @property {string} [unit] - A string representing the unit of measure that will be appended to the formatted number with a non-breaking space.
 * @property {number} [maximumFractionDigits=1] - The maximum number of fraction digits to display. Defaults to 1 if not specified.
 */
export interface NumberFormattingOptions
  extends Omit<Intl.NumberFormatOptions, "locale" | "unit"> {
  locale?: string;
  unit?: string;
}

/**
 * Formats a number to a string with a given locale, unit and number
 * of fractions.
 *
 * @param {number} number - The number to format.
 * @param {NumberFormattingOptions} [options] - The formatting options.
 * @returns {string} The formatted number as string.
 */
const formatNumber = (
  number: number,
  options?: NumberFormattingOptions,
): string => {
  const value = number.toLocaleString(options?.locale || "de-DE", {
    maximumFractionDigits: options?.maximumFractionDigits || 1,
    ...omit(options, ["locale", "unit"]),
  });

  return options?.unit ? `${value}${nonbreakingSpace}${options?.unit}` : value;
};

/**
 * Returns a function Formating a number to a string with the currently
 * chosen locale, unit and number of fractions.
 *
 * @param {number} number - The number to format.
 * @param {NumberFormattingOptions} [options] - The formatting options.
 * @returns {string} The formatted number as string.
 */
export const useFormatNumber = (): ((
  number: number,
  options?: Omit<NumberFormattingOptions, "locale">,
) => string) => {
  const { locale } = useLocale();
  const intlLocale = useMemo(() => intlLocaleForSiteLocale(locale), [locale]);
  return useCallback(
    (number: number, options?: Omit<NumberFormattingOptions, "locale">) => {
      return formatNumber(number, {
        locale: intlLocale,
        ...options,
      });
    },
    [intlLocale],
  );
};
